import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, today } from "@/lib/api";
import { Field, inputCls, textareaCls } from "@/components/common";
import { LineItemsEditor } from "@/components/LineItemsEditor";
import { CustomerPicker } from "@/components/CustomerPicker";
import { useMarkDirty } from "@/components/AppShell";

export function JobDialog({ open, onClose, customerId = null }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  useMarkDirty("job-dialog", open && (items.length > 0 || !!form.notes || !!form.customer_id));
  useEffect(() => {
    if (open) { setForm({ customer_id: customerId || "", job_address: "", scheduled_date: today(), scheduled_time: "09:00", notes: "" }); setItems([]); }
  }, [open, customerId]);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Select a customer");
    setSaving(true);
    try {
      const { data } = await api.post("/jobs", { ...form, line_items: items });
      toast.success(`Job ${data.number} scheduled`);
      qc.invalidateQueries();
      onClose();
      nav(`/jobs/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="job-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Job</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Customer">
            <CustomerPicker value={form.customer_id} testId="job-customer-picker" onChange={(c) => setForm((s) => ({ ...s, customer_id: c?.id || "", job_address: c ? (c.service_address || s.job_address) : s.job_address }))} />
          </Field>
          <Field label="Job address"><input data-testid="job-address-input" className={inputCls} value={form.job_address || ""} onChange={set("job_address")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><input data-testid="job-date-input" type="date" className={inputCls} value={form.scheduled_date || ""} onChange={set("scheduled_date")} /></Field>
            <Field label="Time"><input data-testid="job-time-input" type="time" className={inputCls} value={form.scheduled_time || ""} onChange={set("scheduled_time")} /></Field>
          </div>
          <div><span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Services</span><LineItemsEditor items={items} onChange={setItems} /></div>
          <Field label="Notes"><textarea data-testid="job-notes-input" className={textareaCls} value={form.notes || ""} onChange={set("notes")} /></Field>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1 h-11" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 h-11 btn-orange" data-testid="job-save-button">{saving ? "Saving..." : "Schedule Job"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
